import { createEnumTypeGuard, isNumber, isObject, isString } from '@/utils/validator';

/**
 * Pure classification, parsing, and bounded-recovery policy for lazy-route
 * dynamic-import failures (EDDY-FE-W0).
 *
 * This module is intentionally free of side effects: it does not touch
 * `sessionStorage`, `window`, `navigator`, or the logger. The impure glue in
 * `src/router/utils.tsx` reads/writes storage and reloads based on the
 * {@link RecoveryAction} returned here.
 */

const IMPORT_FAILURE_CATEGORIES = {
  /** HTML served where a JavaScript module was expected (stale/missing chunk answered by the SPA fallback). */
  mime_mismatch: 'mime_mismatch',
  /** Browser could not resolve a dynamically imported module (typically a missing chunk). */
  missing_chunk: 'missing_chunk',
  /** Module source was truncated (`Unexpected EOF`). */
  truncated: 'truncated',
  /** Generic module-fetch failure (`Importing a module script failed.`). */
  generic_fetch: 'generic_fetch',
  /** `navigator.onLine` is false at failure time — reload cannot help. */
  offline: 'offline',
  /** Error did not match any recognized import-failure signature. */
  unknown: 'unknown',
} as const;

/** Coarse classification of a browser-raised dynamic-import error. */
export type ImportFailureCategory = (typeof IMPORT_FAILURE_CATEGORIES)[keyof typeof IMPORT_FAILURE_CATEGORIES];

const isImportFailureCategory = createEnumTypeGuard(IMPORT_FAILURE_CATEGORIES);

/**
 * Maps a browser-raised dynamic-import error to a coarse failure category.
 *
 * Error message text is the only cross-browser signal available; see
 * `docs/post-mortem/EDDY-FE-W0-dynamic-import-failures.md` for the observed
 * production messages.
 */
export function classifyImportError(error: Error): ImportFailureCategory {
  const message = error?.message ?? '';

  if (/mime type/i.test(message) && /html/i.test(message)) {
    return 'mime_mismatch';
  }
  if (/unexpected eof/i.test(message)) {
    return 'truncated';
  }
  // Chrome: "Failed to fetch dynamically imported module: <url>"
  // Firefox: "error loading dynamically imported module: <url>"
  // Both signal a chunk that could not be loaded (typically a missing/404'd
  // chunk after a deployment drops the old hash).
  if (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  ) {
    return 'missing_chunk';
  }

  // Safari: "Importing a module script failed."
  if (/importing a module script failed/i.test(message)) {
    return 'generic_fetch';
  }

  return 'unknown';
}

/** Bounded window during which at most one automatic reload may be attempted. */
export const RECOVERY_COOLDOWN_MS = 10_000;

/** Schema version of the persisted recovery state. */
export const RECOVERY_STATE_VERSION = 1 as const;

/** Shared sessionStorage key for all lazy-import recovery entrypoints. */
export const RECOVERY_STORAGE_KEY = `lazy-import-recovery:v${RECOVERY_STATE_VERSION}`;

/** Versioned recovery state persisted to `sessionStorage` after a reload attempt. */
export interface RecoveryState {
  attemptedAt: number;
  route: string;
  category: ImportFailureCategory;
  /** Application release tag, when the build pipeline injects one. */
  release?: string;
}

export type RecoveryAction =
  | { type: 'reload'; category: ImportFailureCategory }
  | { type: 'surface'; category: ImportFailureCategory };

export interface RecoveryInput {
  now: number;
  online: boolean;
  stored: RecoveryState | null;
  error: Error;
}

/**
 * Decides recovery for a single failed dynamic import.
 *
 * Pure: performs no I/O. The wrapper in `src/router/utils.tsx` persists state
 * and reloads based on the returned {@link RecoveryAction}.
 */
export function decideRecovery(input: RecoveryInput): RecoveryAction {
  if (!input.online) {
    return { type: 'surface', category: 'offline' };
  }

  const category = classifyImportError(input.error);
  if (category === 'unknown') {
    return { type: 'surface', category: 'unknown' };
  }

  const withinCooldown = input.stored != null && input.now - input.stored.attemptedAt < RECOVERY_COOLDOWN_MS;
  if (withinCooldown) {
    return { type: 'surface', category };
  }

  return { type: 'reload', category };
}

/**
 * Safely parses persisted recovery state. Returns `null` for missing, malformed,
 * or unrecognized data so a corrupt read is treated as "no prior attempt"
 * (exactly one reload) rather than the old behavior of reloading on every read.
 */
export function parseRecoveryState(raw: string | null): RecoveryState | null {
  if (raw == null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isObject(parsed)) {
    return null;
  }
  const obj = parsed;

  if (obj.v !== RECOVERY_STATE_VERSION) {
    return null;
  }
  if (!isNumber(obj.attemptedAt) || !Number.isFinite(obj.attemptedAt)) {
    return null;
  }
  if (!isString(obj.route)) {
    return null;
  }
  if (!isImportFailureCategory(obj.category)) {
    return null;
  }
  if (obj.release !== undefined && !isString(obj.release)) {
    return null;
  }

  return {
    attemptedAt: obj.attemptedAt,
    route: obj.route,
    category: obj.category,
    ...(obj.release !== undefined ? { release: obj.release } : {}),
  };
}
